import { useCallback, useState, useEffect } from "react";
import { useImmer } from "use-immer";
import SidebarListItem from "./SidebarListItem";
import { sortHighlightsByLocation } from "@shared/utils/utils";
import { useAppSelector, useAppDispatch } from "@store/hooks";
import {
  selectViewedHighlights,
  selectCategories,
  selectIsEvaluator,
} from "@store/selectors";
import {
  Box,
  Drawer,
  IconButton,
  Typography,
  useTheme,
  ToggleButtonGroup,
  ToggleButton,
  Tabs,
  Tab,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";

import {
  ChevronLeft,
  LastPage,
  FirstPage,
  Add,
  Sort,
} from "@mui/icons-material";

import "@/styles/Sidebar.css";

import FilterCategories from "./FilterCategories";
import Tooltip from "@mui/material/Tooltip";
import SidebarContextMenu from "./SidebarContextMenu";
import CannedResponseItem from "./CannedResponseItem";
import CannedResponseContextMenu from "./CannedResponseContextMenu";
import RubricSection from "./RubricSection";
import GradeSection from "./GradeSection";
import GradingViewBar from "../../reader/components/GradingViewBar";
import CanvasGradingViewBar from "../../canvas/components/CanvasGradingViewBar";
import { ReadingProgress } from "./ReadingProgress";
import {
  addResponse,
  editResponse,
  deleteResponse,
  selectUsername,
} from "@features/annogram/sidebar/state/cannedResponsesSlice";
import { addUserInfo } from "@store/slices/authSlice";
import {
  selectSidebarMode,
  setSidebarMode,
} from "@store/slices/viewSlice";
import { TOUR } from "@features/onboarding/tourTargets";
import { CSSObject } from "@mui/material/styles";

interface Props {
  handleHighlightDragStart: (id: string) => () => void;
  isNonMobile: boolean;
  drawerWidth: string;
  isSidebarOpen: boolean;
  closeSidebar: () => void;
  pageCount: number;
  handlePageIncrease: () => void;
  handlePageDecrease: () => void;
  currentPage: number;
  handlePageChange: (page: number) => void;
  setSidebarWidth: (width: number) => void;
  canvasOn: boolean;
}

// Constants for min/max sidebar width
const MIN_SIDEBAR_WIDTH = 250;
const MAX_SIDEBAR_WIDTH = 800;
const DEFAULT_SIDEBAR_WIDTH = 400;

// Custom scrollbar styling that responds to theme
const getCustomScrollbar = (theme: any): CSSObject => ({
  "&::-webkit-scrollbar": {
    width: "8px",
    backgroundColor: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(255, 255, 255, 0.2)"
        : "rgba(0, 0, 0, 0.2)",
    borderRadius: "4px",
    "&:hover": {
      backgroundColor:
        theme.palette.mode === "dark"
          ? "rgba(255, 255, 255, 0.3)"
          : "rgba(0, 0, 0, 0.3)",
    },
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: "transparent",
  },
  // Firefox scrollbar styling
  scrollbarWidth: "thin",
  scrollbarColor: `${
    theme.palette.mode === "dark"
      ? "rgba(255, 255, 255, 0.2)"
      : "rgba(0, 0, 0, 0.2)"
  } transparent`,
});

export default function ArgSidebar({
  isSidebarOpen,
  closeSidebar,
  isNonMobile,
  drawerWidth,
  pageCount,
  handlePageDecrease,
  handlePageIncrease,
  currentPage,
  handlePageChange,
  setSidebarWidth,
  handleHighlightDragStart,
  canvasOn,
}: Props) {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const isEvaluator = useAppSelector(selectIsEvaluator);
  const sidebarMode = useAppSelector(selectSidebarMode);
  const baseView = useAppSelector((s) => s.view.baseView);
  const [activeAdminTab, setActiveAdminTab] = useState(0);

  // Annotators can never see the Evaluate view; reset if tier drops.
  useEffect(() => {
    if (!isEvaluator && sidebarMode === "evaluate") {
      dispatch(setSidebarMode("annotate"));
    }
  }, [dispatch, isEvaluator, sidebarMode]);
  const [isResizing, setIsResizing] = useState(false);

  // Get canned responses from Redux
  const { responses: cannedResponses } = useAppSelector(
    (state) => state.cannedResponses
  );

  // Get current username from auth state
  const username = useAppSelector(selectUsername);

  // New response dialog state
  const [newResponseDialogOpen, setNewResponseDialogOpen] = useState(false);
  const [newResponseTitle, setNewResponseTitle] = useState("");
  const [newResponseContent, setNewResponseContent] = useState("");

  // Context menu for responses
  const [contextMenuResponseId, setContextMenuResponseId] =
    useState<string>("");
  const [contextMenuCoords, setContextMenuCoords] = useImmer({ x: 0, y: 0 });

  // Existing context menu state for highlights
  const highlights = useAppSelector(selectViewedHighlights);
  const categories = useAppSelector(selectCategories);

  const userState = useAppSelector((state) => state.user);

  const [categoryFilter, setCategoryFilter] = useState("None");
  // Sidebar list direction: "asc" = document order (page 1, top first),
  // "desc" = reversed (end of document first).
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [coords, setCoords] = useImmer({ x: 0, y: 0 });
  const [contextMenuId, setContextMenuId] = useState<string>("");

  // Create the theme-aware scrollbar styles
  const customScrollbar = getCustomScrollbar(theme);

  // Handlers for highlights
  const handleHighlightContextMenu =
    (id: string) => (event: React.MouseEvent<HTMLDivElement>) => {
      setContextMenuId(id);
      setCoords({ x: event.clientX + 2, y: event.clientY - 6 });
    };

  const handleCloseContextMenu = useCallback(() => setContextMenuId(""), []);

  // Admin view handlers
  const handleViewModeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newMode: "annotate" | "evaluate" | null
  ) => {
    if (newMode !== null) {
      dispatch(setSidebarMode(newMode));
    }
  };

  const handleAdminTabChange = (
    event: React.SyntheticEvent,
    newValue: number
  ) => {
    setActiveAdminTab(newValue);
  };

  // Canned responses handlers
  const handleCannedResponseSelect = (id: string) => {
    // Implementation will be added later
  };

  const handleCannedResponseContextMenu = (
    id: string,
    event: React.MouseEvent<HTMLDivElement>
  ) => {
    setContextMenuResponseId(id);
    setContextMenuCoords({ x: event.clientX + 2, y: event.clientY - 6 });
  };

  const handleCloseResponseContextMenu = useCallback(
    () => setContextMenuResponseId(""),
    []
  );

  const handleAddNewResponse = () => {
    // Check if user info is loaded, load it if not
    if (userState.userId) {
      dispatch(addUserInfo(userState.userId));
    }

    setNewResponseTitle("");
    setNewResponseContent("");
    setNewResponseDialogOpen(true);
  };

  const handleSaveNewResponse = () => {
    if (newResponseTitle.trim() && newResponseContent.trim()) {
      dispatch(
        addResponse({
          title: newResponseTitle,
          content: newResponseContent,
          userName: username,
        })
      );
      setNewResponseDialogOpen(false);
    }
  };

  const handleEditResponse = (
    id: string,
    updatedResponse: { title?: string; content?: string }
  ) => {
    dispatch(
      editResponse({
        id,
        ...updatedResponse,
      })
    );
  };

  const handleDeleteResponse = (id: string) => {
    dispatch(deleteResponse({ id }));
  };

  // Resizing functionality
  const handleResizeStart = (e: React.MouseEvent) => {
    setIsResizing(true);
    e.preventDefault();
  };

  const handleResizeMove = useCallback(
    (e: globalThis.MouseEvent) => {
      if (isResizing) {
        const newWidth = e.clientX;
        const maxWidth = Math.min(MAX_SIDEBAR_WIDTH, window.innerWidth / 2);
        if (newWidth >= MIN_SIDEBAR_WIDTH && newWidth <= maxWidth) {
          setSidebarWidth(newWidth);
          // Force reflow to apply new width immediately
          document.body.style.cursor = "ew-resize";
        } else if (newWidth < MIN_SIDEBAR_WIDTH) {
          closeSidebar();
          setSidebarWidth(DEFAULT_SIDEBAR_WIDTH);
        }
      }
    },
    [isResizing, setSidebarWidth, closeSidebar]
  );

  const handleResizeEnd = useCallback(() => {
    setIsResizing(false);
    document.body.style.cursor = "";
  }, []);

  // Set up and clean up event listeners
  useEffect(() => {
    if (isResizing) {
      document.addEventListener("mousemove", handleResizeMove);
      document.addEventListener("mouseup", handleResizeEnd);
    }

    return () => {
      document.removeEventListener("mousemove", handleResizeMove);
      document.removeEventListener("mouseup", handleResizeEnd);
    };
  }, [isResizing, handleResizeMove, handleResizeEnd]);

  // sortHighlightsByLocation returns a fresh copy, so reversing in place
  // for descending is safe.
  const sortedHighlights = sortHighlightsByLocation(highlights);
  if (sortDirection === "desc") sortedHighlights.reverse();

  return (
    <Drawer
      data-tour={TOUR.sidebar}
      open={isSidebarOpen}
      onClose={closeSidebar}
      variant="persistent"
      anchor="left"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        height: "100vh",
        "& .MuiDrawer-paper": {
          boxSizing: "border-box",
          borderWidth: isNonMobile ? 0 : "2px",
          width: drawerWidth,
          position: "relative",
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          overflowY: "hidden",
          backgroundColor: theme.palette.background.paper,
          boxShadow: theme.shadows[4],
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "10px 24px",
          borderBottom: `1px solid ${theme.palette.divider}`,
          flexShrink: 0,
        }}
      >
        {userState.userId && isEvaluator ? (
          <ToggleButtonGroup
            value={sidebarMode}
            exclusive
            onChange={handleViewModeChange}
            aria-label="view mode"
            size="small"
          >
            <ToggleButton value="annotate" aria-label="annotate view">
              Annotate
            </ToggleButton>
            <ToggleButton value="evaluate" aria-label="evaluate view">
              Evaluate
            </ToggleButton>
          </ToggleButtonGroup>
        ) : (
          <Box />
        )}

        <Tooltip title="Close Sidebar">
          <IconButton onClick={closeSidebar} sx={{ justifySelf: "end" }}>
            <ChevronLeft />
          </IconButton>
        </Tooltip>
      </Box>

      {sidebarMode === "annotate" || !userState.userId || !isEvaluator ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              visibility: canvasOn ? "hidden" : "visible",
              px: 2,
              mb: 1,
              flexShrink: 0,
            }}
          >
            <Tooltip title="First Page">
              <IconButton onClick={handlePageDecrease}>
                <FirstPage />
              </IconButton>
            </Tooltip>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                m: 1,
              }}
            >
              <TextField
                sx={{ width: "100px" }}
                label={currentPage}
                inputProps={{ min: 1, max: pageCount }}
                variant="outlined"
                type="number"
                size="small"
                onFocus={(e) => {
                  e.target.value = "";
                }}
                onChange={(e) => {
                  e.preventDefault();
                  handlePageChange(parseInt(e.target.value));
                }}
              />
              <Typography
                color={theme.palette.text.primary}
                sx={{ marginLeft: "10px" }}
              >
                of {pageCount}
              </Typography>
            </Box>
            <Tooltip title={"Last Page"}>
              <IconButton onClick={handlePageIncrease}>
                <LastPage />
              </IconButton>
            </Tooltip>
          </Box>

          <Box sx={{ px: 2, flexShrink: 0 }}>
            <ReadingProgress />
          </Box>

          <Box
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 10,
              backgroundColor: theme.palette.background.paper,
              paddingBottom: "8px",
              borderBottom: `1px solid ${theme.palette.divider}`,
              marginBottom: "12px",
              display: "flex",
              justifyContent: "center",
              width: "100%",
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                width: "100%",
                maxWidth: "400px",
                mx: "auto",
                px: 2,
                display: "flex",
                alignItems: "flex-start",
                gap: 1,
              }}
            >
              <Box sx={{ flexGrow: 1 }}>
                <FilterCategories setCategoryFilter={setCategoryFilter} />
              </Box>
              <Tooltip
                title={
                  sortDirection === "asc"
                    ? "Sorted top of document first — click to reverse"
                    : "Sorted end of document first — click to reverse"
                }
              >
                {/* One Sort glyph flipped per direction: MUI's icon reads
                    descending (bars shrinking downward); its vertical
                    mirror is the conventional ascending rendering. */}
                <IconButton
                  onClick={() =>
                    setSortDirection((d) => (d === "asc" ? "desc" : "asc"))
                  }
                  aria-label={
                    sortDirection === "asc"
                      ? "Sorted top of document first; click to show end of document first"
                      : "Sorted end of document first; click to show top of document first"
                  }
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    // Center against the filter Select (its helper text
                    // hangs below the input).
                    mt: "10px",
                    p: "8px",
                  }}
                >
                  <Sort
                    fontSize="small"
                    sx={
                      sortDirection === "asc"
                        ? { transform: "scaleY(-1)" }
                        : undefined
                    }
                  />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <Box
            sx={{
              flexGrow: 1,
              overflowY: "auto",
              px: 2,
              pb: 1,
              pt: 1,
              ...customScrollbar,
            }}
          >
            {sortedHighlights.map((highlight) => (
              <SidebarListItem
                categoryFilter={categoryFilter}
                highlight={highlight}
                key={highlight.id}
                categories={categories}
                handleContextMenu={handleHighlightContextMenu(highlight.id)}
                handleDragStart={handleHighlightDragStart(highlight.id)}
              />
            ))}
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            padding: "16px 24px",
            height: "calc(100vh - 70px)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Typography variant="h6" sx={{ mb: 3 }}>
            Evaluate
          </Typography>

          {/* Contributor selector docks here when the sidebar is open
              (it floats over the PDF/canvas when the sidebar is closed).
              Gated on isSidebarOpen so it never co-exists with the float. */}
          {isSidebarOpen &&
            (baseView === "canvas" ? (
              <CanvasGradingViewBar inline />
            ) : (
              <GradingViewBar inline />
            ))}

          <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
            <Tabs
              value={activeAdminTab}
              onChange={handleAdminTabChange}
              aria-label="admin navigation tabs"
              variant="fullWidth"
              sx={{
                "& .MuiTab-root": {
                  color: theme.palette.text.primary,
                  "&.Mui-selected": {
                    color: theme.palette.text.primary,
                    fontWeight: "bold",
                    backgroundColor: "transparent",
                  },
                },
                "& .MuiTabs-indicator": {
                  backgroundColor: theme.palette.secondary.main,
                },
              }}
            >
              <Tab label="Canned Responses" />
              <Tab label="Rubric" />
              <Tab label="Grade" />
            </Tabs>
          </Box>

          <Box
            sx={{
              flexGrow: 1,
              overflowY: "auto",
              mx: 0,
              px: 2,
              ...customScrollbar,
            }}
          >
            {activeAdminTab === 0 && (
              <Box sx={{ height: "100%" }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 3,
                  }}
                >
                  <Typography variant="subtitle1">
                    Available Responses
                  </Typography>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<Add />}
                    onClick={handleAddNewResponse}
                  >
                    Add New
                  </Button>
                </Box>

                <Box
                  sx={{
                    overflow: "visible",
                    pr: 0,
                  }}
                >
                  {cannedResponses.map((response) => (
                    <CannedResponseItem
                      key={response.id}
                      id={response.id}
                      title={response.title}
                      content={response.content}
                      userName={response.userName}
                      onSelect={handleCannedResponseSelect}
                      onContextMenu={handleCannedResponseContextMenu}
                    />
                  ))}
                </Box>
              </Box>
            )}
            {activeAdminTab === 1 && <RubricSection />}
            {activeAdminTab === 2 && <GradeSection />}
          </Box>
        </Box>
      )}

      <Box
        sx={{
          position: "absolute",
          height: "100%",
          width: "8px",
          right: "-4px",
          top: 0,
          cursor: "ew-resize",
          backgroundColor: "transparent",
          transition: "background-color 0.2s",
          "&:hover": {
            backgroundColor: theme.palette.divider,
          },
          "&:active": {
            backgroundColor: theme.palette.primary.light,
          },
          zIndex: 1300,
        }}
        onMouseDown={handleResizeStart}
      />

      <Dialog
        open={newResponseDialogOpen}
        onClose={() => setNewResponseDialogOpen(false)}
      >
        <DialogTitle>Add New Canned Response</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Title"
            fullWidth
            variant="outlined"
            value={newResponseTitle}
            onChange={(e) => setNewResponseTitle(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Content"
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            value={newResponseContent}
            onChange={(e) => setNewResponseContent(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewResponseDialogOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleSaveNewResponse} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {contextMenuResponseId && (
        <CannedResponseContextMenu
          coords={contextMenuCoords}
          responseId={contextMenuResponseId}
          closeMenu={handleCloseResponseContextMenu}
          onEdit={handleEditResponse}
          onDelete={handleDeleteResponse}
          responses={cannedResponses}
        />
      )}

      {contextMenuId && (
        <SidebarContextMenu
          coords={coords}
          menuTargetId={contextMenuId}
          closeMenu={handleCloseContextMenu}
        />
      )}
    </Drawer>
  );
}
